import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router-dom';
import NavBar from '../components/NavBar';
import RankingPageModal from '../components/RankingPageModal';
import FriendsModal from '../components/FriendsModal';
import TutorialModal from '../components/TutorialModal';
import ProfilePage from './ProfilePage';
import AddFilmFlow from './AddFilmFlow';
import FeedPage from './FeedPage';
import ForYouPage from './ForYouPage';
import SettingsPage from './SettingsPage';
import AdminReportsPage from './AdminReportsPage';
import { userTabInitials } from '../utils/userInitials';
import { effectiveRankingBin, isRankedPost } from '../utils/rankingBins';
import { isTutorialMarkedSeen, markTutorialSeen } from '../utils/tutorialStorage';
import { useToast } from '../context/ToastContext';

async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch (_e) {
    return {};
  }
}

export default function MainAppShell({ onLogout, user, onUserUpdate }) {
  const { showToast } = useToast();
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  const [showTutorialModal, setShowTutorialModal] = useState(false);

  const dismissTutorial = useCallback(() => {
    if (user?.id) markTutorialSeen(user.id);
    setShowTutorialModal(false);
  }, [user?.id]);

  React.useEffect(() => {
    if (!user?.id) return;
    if (!isTutorialMarkedSeen(user.id)) {
      setShowTutorialModal(true);
    }
  }, [user?.id]);
  const [showRanking, setShowRanking] = useState(false);
  const [posts, setPosts] = useState([]);
  const [list, setList] = useState([]);
  const [pendingMovie, setPendingMovie] = useState(null);
  const [addFlowKey, setAddFlowKey] = useState(0);
  const [feedRefreshKey, setFeedRefreshKey] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const isSettingsRoute = Boolean(useMatch('/settings/*'));
  const isAddRoute = Boolean(useMatch('/add/*'));
  const isProfileRoute = Boolean(useMatch('/profile/*'));
  const isOtherProfileRoute = Boolean(useMatch('/profile/:userId'));
  const isFeedRoute = Boolean(useMatch('/feed/*'));
  const isForYouRoute = Boolean(useMatch('/foryou/*'));

  const profileInitials = useMemo(() => userTabInitials(user), [user]);

  React.useEffect(() => {
    // Ranking can run from /add, /feed, or /profile overlays.
    const rankingOkHere = isAddRoute || isFeedRoute || isProfileRoute || isForYouRoute;
    if (!rankingOkHere && showRanking && !pendingMovie?.isRerank) {
      setShowRanking(false);
      setPendingMovie(null);
    }
  }, [isAddRoute, isFeedRoute, isProfileRoute, isForYouRoute, showRanking, pendingMovie?.isRerank]);

  const [friendLists, setFriendLists] = useState({
    friends: [],
    incoming: [],
    outgoing: [],
  });
  const [friendListsError, setFriendListsError] = useState(null);
  const friendListFetchGen = useRef(0);
  const friendIdsSignatureRef = useRef(null);

  const loadFriendLists = useCallback(async () => {
    if (!user?.id) return;
    const gen = ++friendListFetchGen.current;
    setFriendListsError(null);
    try {
      const [fr, inc, out] = await Promise.all([
        fetch('/api/friends', { credentials: 'include' }),
        fetch('/api/friends/incoming', { credentials: 'include' }),
        fetch('/api/friends/outgoing', { credentials: 'include' }),
      ]);
      const [fj, ij, oj] = await Promise.all([
        readJsonSafe(fr),
        readJsonSafe(inc),
        readJsonSafe(out),
      ]);
      if (gen !== friendListFetchGen.current) return;
      if (!fr.ok || !fj.ok) throw new Error(fj.error || 'Failed to load friends');
      if (!inc.ok || !ij.ok) throw new Error(ij.error || 'Failed to load requests');
      if (!out.ok || !oj.ok) throw new Error(oj.error || 'Failed to load outgoing');
      const nextFriends = fj.results || [];
      const nextFriendIdsSignature = nextFriends
        .map((x) => String(x.user?.id || ''))
        .filter(Boolean)
        .sort()
        .join('|');
      if (
        friendIdsSignatureRef.current !== null &&
        friendIdsSignatureRef.current !== nextFriendIdsSignature
      ) {
        setFeedRefreshKey((k) => k + 1);
      }
      friendIdsSignatureRef.current = nextFriendIdsSignature;
      setFriendLists({
        friends: nextFriends,
        incoming: ij.results || [],
        outgoing: oj.results || [],
      });
    } catch (err) {
      console.error(err);
      if (gen !== friendListFetchGen.current) return;
      setFriendListsError((err && err.message) || 'Could not load friends data');
    }
  }, [user?.id]);

  React.useEffect(() => {
    if (isFeedRoute && user?.id) {
      loadFriendLists();
    }
  }, [isFeedRoute, user?.id, loadFriendLists]);

  React.useEffect(() => {
    if (isOtherProfileRoute && user?.id) {
      loadFriendLists();
    }
  }, [isOtherProfileRoute, user?.id, loadFriendLists]);

  React.useEffect(() => {
    if (showFriendsModal && user?.id) {
      loadFriendLists();
    }
  }, [showFriendsModal, user?.id, loadFriendLists]);

  const otherProfileCache = useRef(new Map());

  const openUserProfile = useCallback(
    (uid) => {
      const id = uid ? String(uid) : '';
      if (!id) return;

      // Navigate immediately (avoids flashing the feed after modal closes),
      // then fetch in the background; the route renders a blank aria-busy shell.
      navigate(`/profile/${encodeURIComponent(id)}`);

      if (!otherProfileCache.current.has(id)) {
        otherProfileCache.current.set(id, { status: 'loading' });
      }
      const entry = otherProfileCache.current.get(id);
      if (entry?.status === 'loading') {
        Promise.all([
          fetch(`/api/users/${encodeURIComponent(id)}`, { credentials: 'include' }),
          fetch(`/api/users/${encodeURIComponent(id)}/activity`, { credentials: 'include' }),
        ])
          .then(async ([ur, ar]) => {
            const [uj, aj] = await Promise.all([readJsonSafe(ur), readJsonSafe(ar)]);
            if (!ur.ok || !uj.ok || !ar.ok || !aj.ok) {
              otherProfileCache.current.set(id, { status: 'error' });
              return;
            }
            otherProfileCache.current.set(id, {
              status: 'ready',
              user: uj.user || null,
              posts: aj.results || [],
            });
          })
          .catch(() => {
            otherProfileCache.current.set(id, { status: 'error' });
          });
      }
    },
    [navigate],
  );

  const loadPosts = React.useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await fetch('/api/watchedmovies', { credentials: 'include' });
      const data = await res.json();
      if (res.ok && data.ok) {
        setPosts(data.results || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [user?.id]);

  const loadList = React.useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(`/api/watchlist?userId=${encodeURIComponent(user.id)}`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setList(data.results || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [user?.id]);

  React.useEffect(() => {
    loadPosts();
    loadList();
  }, [loadPosts, loadList]);

  const existingMoviesForTier = useMemo(() => {
    const bin = pendingMovie?.rankingBin;
    if (!bin) return [];
    return posts
      .filter(
        (p) =>
          isRankedPost(p) &&
          effectiveRankingBin(p) === bin && p.movieId !== pendingMovie?.movieId
      )
      .sort((a, b) => (a.rankPosition ?? 0) - (b.rankPosition ?? 0));
  }, [posts, pendingMovie?.rankingBin, pendingMovie?.movieId]);

  return (
    <div className="flique-app min-vh-100 pb-5">
      <NavBar
        onGoAdd={() => navigate('/add')}
        onMenu={() => navigate('/settings', { state: { from: location.pathname } })}
        onHelp={() => setShowTutorialModal(true)}
        showBack={isSettingsRoute || isOtherProfileRoute}
        onBack={() => {
          if (isOtherProfileRoute) {
            const idx = window.history?.state?.idx;
            if (typeof idx === 'number' && idx > 0) {
              navigate(-1);
              return;
            }
            navigate('/feed', { replace: true });
            return;
          }
          const from = location.state?.from;
          if (typeof from === 'string' && from.startsWith('/')) {
            navigate(from);
            return;
          }
          const idx = window.history?.state?.idx;
          if (typeof idx === 'number' && idx > 0) {
            navigate(-1);
            return;
          }
          navigate('/add', { replace: true });
        }}
      />
      <main className="container py-4">
        <Routes>
          <Route
            path="/profile"
            element={
              <ProfilePage
                user={user}
                posts={posts}
                list={list}
                viewerPosts={posts}
                viewerList={list}
                onAddToWatchMovieList={async (movie) => {
                  try {
                    const res = await fetch('/api/addtowatchmovielist', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({
                        selectedMovie: movie,
                        userId: user?.id,
                      }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.ok) {
                      throw new Error(data.error || 'Failed to add movie');
                    }
                    await loadList();
                  } catch (err) {
                    console.error(err);
                  }
                }}
                onRemoveMovie={async (postId) => {
                  try {
                    const res = await fetch('/api/removewatchedmovie', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ watchedMovieId: postId }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.ok) {
                      throw new Error(data.error || 'Failed to remove movie');
                    }
                    await loadPosts();
                  } catch (err) {
                    console.error(err);
                  }
                }}
                onRemoveWatchlistEntry={async (watchlistEntryId) => {
                  try {
                    const res = await fetch('/api/removefromwatchlist', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ watchlistEntryId }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.ok) {
                      throw new Error(data.error || 'Failed to remove from watchlist');
                    }
                    await loadList();
                  } catch (err) {
                    console.error(err);
                  }
                }}
                onBeginRanking={(watchedMovie) => {
                  setPendingMovie(watchedMovie);
                  setShowRanking(true);
                }}
                onRankAgain={async (post) => {
                  setPendingMovie({
                    selectedMovie: {
                      title: post.movie?.title,
                      year: post.movie?.year,
                      poster_url: post.movie?.poster_url,
                    },
                    watchedAt: post.watchedAt,
                    rankingBin: post.rankingBin,
                    movieId: post.movieId,
                    isRerank: true,
                    afterRankNavigate: '/profile',
                  });
                  setShowRanking(true);
                }}
              />
            }
          />
          <Route
            path="/profile/:userId"
            element={
              <OtherProfileRoute
                viewerId={user?.id}
                friendLists={friendLists}
                onReloadFriendLists={loadFriendLists}
                cacheRef={otherProfileCache}
                viewerPosts={posts}
                viewerList={list}
                onAddToWatchMovieList={async (movie) => {
                  try {
                    const res = await fetch('/api/addtowatchmovielist', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({
                        selectedMovie: movie,
                        userId: user?.id,
                      }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.ok) {
                      throw new Error(data.error || 'Failed to add movie');
                    }
                    await loadList();
                  } catch (err) {
                    console.error(err);
                  }
                }}
                onRemoveFromWatchlist={async (watchlistEntryId) => {
                  try {
                    const res = await fetch('/api/removefromwatchlist', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ watchlistEntryId }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.ok) {
                      throw new Error(data.error || 'Failed to remove from watchlist');
                    }
                    await loadList();
                  } catch (err) {
                    console.error(err);
                  }
                }}
                onBeginRanking={(watchedMovie) => {
                  setPendingMovie(watchedMovie);
                  setShowRanking(true);
                }}
              />
            }
          />
          <Route
            path="/add"
            element={
              <AddFilmFlow
                key={addFlowKey}
                posts={posts}
                watchlist={list}
                onBeginRanking={(watchedMovie) => {
                  setPendingMovie(watchedMovie);
                  setShowRanking(true);
                }}
                onAddToWatchMovieList={async (movie) => {
                  try {
                    const res = await fetch('/api/addtowatchmovielist', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({
                        selectedMovie: movie,
                        userId: user?.id,
                      }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.ok) {
                      throw new Error(data.error || 'Failed to add watched movie');
                    }
                    await loadList();
                  } catch (err) {
                    console.error(err);
                  }
                }}
                onRemoveFromWatchlist={async (watchlistEntryId) => {
                  try {
                    const res = await fetch('/api/removefromwatchlist', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ watchlistEntryId }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.ok) {
                      throw new Error(data.error || 'Failed to remove from watchlist');
                    }
                    await loadList();
                  } catch (err) {
                    console.error(err);
                  }
                }}
              />
            }
          />
          <Route
            path="/feed"
            element={
              <FeedPage
                feedRefreshKey={feedRefreshKey}
                onOpenFriends={() => setShowFriendsModal(true)}
                onOpenProfile={openUserProfile}
                hasPendingIncomingRequests={(friendLists.incoming || []).length > 0}
                currentUserId={user?.id}
                viewerProfileVisibility={user?.profile_visibility}
                viewerPosts={posts}
                viewerList={list}
                onAddToWatchMovieList={async (movie) => {
                  try {
                    const res = await fetch('/api/addtowatchmovielist', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({
                        selectedMovie: movie,
                        userId: user?.id,
                      }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.ok) {
                      throw new Error(data.error || 'Failed to add movie');
                    }
                    await loadList();
                  } catch (err) {
                    console.error(err);
                  }
                }}
                onRemoveFromWatchlist={async (watchlistEntryId) => {
                  try {
                    const res = await fetch('/api/removefromwatchlist', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ watchlistEntryId }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.ok) {
                      throw new Error(data.error || 'Failed to remove from watchlist');
                    }
                    await loadList();
                  } catch (err) {
                    console.error(err);
                  }
                }}
                onBeginRanking={(watchedMovie) => {
                  setPendingMovie({
                    ...watchedMovie,
                    afterRankNavigate: '/feed',
                  });
                  setShowRanking(true);
                }}
              />
            }
          />
          <Route
            path="/settings"
            element={
              <SettingsPage
                user={user}
                onLogout={onLogout}
                onUserUpdate={onUserUpdate}
              />
            }
          />
          <Route
            path="/foryou"
            element={
              <ForYouPage
                viewerPosts={posts}
                viewerList={list}
                onAddToWatchMovieList={async (movie) => {
                  try {
                    const res = await fetch('/api/addtowatchmovielist', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ selectedMovie: movie, userId: user?.id }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to add movie');
                    await loadList();
                  } catch (err) {
                    console.error(err);
                  }
                }}
                onRemoveFromWatchlist={async (watchlistEntryId) => {
                  try {
                    const res = await fetch('/api/removefromwatchlist', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ watchlistEntryId }),
                    });
                    const data = await res.json();
                    if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to remove from watchlist');
                    await loadList();
                  } catch (err) {
                    console.error(err);
                  }
                }}
                onBeginRanking={(watchedMovie) => {
                  setPendingMovie({ ...watchedMovie, afterRankNavigate: '/foryou' });
                  setShowRanking(true);
                }}
              />
            }
          />
          <Route
            path="/admin/reports"
            element={
              user?.isAdmin ? (
                <AdminReportsPage />
              ) : (
                <Navigate to="/add" replace />
              )
            }
          />
          <Route path="*" element={<Navigate to="/add" replace />} />
        </Routes>
      </main>

      <FriendsModal
        open={showFriendsModal}
        onClose={() => setShowFriendsModal(false)}
        onOpenProfile={openUserProfile}
        currentUserId={user?.id}
        friends={friendLists.friends}
        incoming={friendLists.incoming}
        outgoing={friendLists.outgoing}
        friendListsError={friendListsError}
        onReloadFriendLists={loadFriendLists}
      />

      <TutorialModal
        open={showTutorialModal}
        onClose={dismissTutorial}
      />

      {showRanking && pendingMovie?.rankingBin && (
        <RankingPageModal
          newMovie={pendingMovie}
          rankingBin={pendingMovie.rankingBin}
          existingMovies={existingMoviesForTier}
          onBackToTierSelection={() => {
            const goBack = pendingMovie?.onBackToTierSelection;
            if (typeof goBack === 'function') {
              setShowRanking(false);
              setPendingMovie(null);
              goBack();
            }
          }}

          onDone={async ({ insertionIndex, notes, hideFromFeed }) => {
            const returnAfterRank = pendingMovie?.afterRankNavigate;
            try {
              const endpoint = pendingMovie?.isRerank
                ? '/api/rerankmovie' : '/api/addwatchedmovie';
              const requestBody = pendingMovie?.isRerank
                ? {movieId: pendingMovie.movieId,
                    rankPosition: insertionIndex + 1,
                    rankingBin: pendingMovie.rankingBin,}
                : {selectedMovie: pendingMovie.selectedMovie,
                    watchedAt: pendingMovie.watchedAt,
                    userId: user?.id,
                    rankPosition: insertionIndex + 1,
                    rankingBin: pendingMovie.rankingBin,
                    ...(notes ? { notes } : {}),
                    ...(hideFromFeed ? { hideFromFeed } : {}),};
              const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(requestBody),
              });
              const data = await res.json();
              if (!res.ok || !data.ok) {
                throw new Error(data.error || 'Failed to add watched movie');
              }
              await Promise.all([loadPosts(), loadList()]);
              const loggedTitle = (pendingMovie?.selectedMovie?.title || '').trim();
              const isRerank = Boolean(pendingMovie?.isRerank);
              showToast(
                isRerank
                  ? loggedTitle
                    ? `${loggedTitle} reranked`
                    : 'Movie reranked'
                  : loggedTitle
                    ? `${loggedTitle} logged`
                    : 'Movie logged',
                {
                  variant: isRerank ? 'rerank' : 'celebrate',
                },
              );
              setShowRanking(false);
              setPendingMovie(null);
              const nextPath =
                typeof returnAfterRank === 'string' &&
                returnAfterRank.startsWith('/')
                  ? returnAfterRank
                  : '/add';
              navigate(nextPath);
              if (nextPath === '/feed') {
                setFeedRefreshKey((k) => k + 1);
              }
              if (nextPath === '/add') {
                setAddFlowKey((prev) => prev + 1);
              }
            } catch (err) {
              console.error(err);
            }
          }}
        
          onExit={() => {
            setShowRanking(false);
            setPendingMovie(null);
          }}
        />
      )}

      <div className="flique-tabbar">
        <div className="flique-tabbar-inner flique-tabbar-inner--four">
          <button
            type="button"
            className={`btn flique-tab ${
              isFeedRoute
                ? 'active'
                : isSettingsRoute && location.state?.from === '/feed'
                    ? 'flique-tab-back-target'
                    : ''
            }`}
            onClick={() => navigate('/feed')}
            aria-label="Home"
          >
            <span className="flique-tab-icon flique-tab-icon-home" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3v3H6v-3Z"
                />
              </svg>
            </span>
          </button>
          <button
            type="button"
            className={`btn flique-tab ${
              isAddRoute
                ? 'active'
                : isSettingsRoute && location.state?.from === '/add'
                    ? 'flique-tab-back-target'
                    : ''
            }`}
            onClick={() => navigate('/add')}
            aria-label="Add a movie"
          >
            <span className="flique-tab-icon flique-tab-icon-plus">+</span>
          </button>
          <button
            type="button"
            className={`btn flique-tab ${
              isForYouRoute
                ? 'active'
                : isSettingsRoute && location.state?.from === '/foryou'
                    ? 'flique-tab-back-target'
                    : ''
            }`}
            onClick={() => navigate('/foryou')}
            aria-label="For You"
          >
            <span className="flique-tab-icon flique-tab-icon-foryou" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z"
                />
              </svg>
            </span>
          </button>
          <button
            type="button"
            className={`btn flique-tab ${
              isProfileRoute
                ? 'active'
                : isSettingsRoute && location.state?.from === '/profile'
                    ? 'flique-tab-back-target'
                    : ''
            }`}
            onClick={() => navigate('/profile')}
          >
            <span className="flique-tab-icon flique-tab-icon-avatar">{profileInitials}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function OtherProfileRoute({
  viewerId,
  friendLists,
  onReloadFriendLists,
  cacheRef,
  viewerPosts,
  viewerList,
  onAddToWatchMovieList,
  onRemoveFromWatchlist,
  onBeginRanking,
}) {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [profilePosts, setProfilePosts] = useState([]);
  const loadGen = useRef(0);

  React.useEffect(() => {
    const uid = (userId || '').trim();
    if (!uid) {
      setErr('User not found');
      setProfileUser(null);
      setProfilePosts([]);
      setLoading(false);
      return;
    }

    const cached = cacheRef?.current?.get(uid);
    if (cached?.status === 'ready') {
      setErr(null);
      setProfileUser(cached.user || null);
      setProfilePosts(cached.posts || []);
      setLoading(false);
      return;
    }
    if (cached?.status === 'error') {
      setErr('User not found');
      setProfileUser(null);
      setProfilePosts([]);
      setLoading(false);
      return;
    }

    const gen = ++loadGen.current;
    setLoading(true);
    setErr(null);
    setProfileUser(null);
    setProfilePosts([]);
    (async () => {
      try {
        const [ur, ar] = await Promise.all([
          fetch(`/api/users/${uid}`, { credentials: 'include' }),
          fetch(`/api/users/${uid}/activity`, { credentials: 'include' }),
        ]);
        const [uj, aj] = await Promise.all([readJsonSafe(ur), readJsonSafe(ar)]);
        if (gen !== loadGen.current) return;
        if (!ur.ok || !uj.ok) throw new Error('User not found');
        if (!ar.ok || !aj.ok) throw new Error('User not found');
        setProfileUser(uj.user || null);
        setProfilePosts(aj.results || []);
      } catch (_e) {
        if (gen === loadGen.current) {
          setErr('User not found');
        }
      } finally {
        if (gen === loadGen.current) {
          setLoading(false);
        }
      }
    })();
  }, [userId, cacheRef]);

  if (loading) {
    return <div aria-busy="true" aria-label="Loading profile" />;
  }
  if (err || !profileUser) {
    return <div className="text-muted small">{err || 'User not found'}</div>;
  }
  return (
    <ProfilePage
      user={profileUser}
      posts={profilePosts}
      viewerPosts={viewerPosts}
      viewerList={viewerList}
      onAddToWatchMovieList={onAddToWatchMovieList}
      onRemoveWatchlistEntry={onRemoveFromWatchlist}
      onBeginRanking={onBeginRanking}
      isSelf={false}
      relationshipNode={
        <RelationshipSection
          viewerId={viewerId}
          targetUser={profileUser}
          friendLists={friendLists}
          onReloadFriendLists={onReloadFriendLists}
        />
      }
    />
  );
}

function RelationshipSection({ viewerId, targetUser, friendLists, onReloadFriendLists }) {
  const targetId = targetUser?.id ? String(targetUser.id) : null;
  const targetUsername = String(targetUser?.username || 'user').trim() || 'user';
  const friends = friendLists?.friends || [];
  const incoming = friendLists?.incoming || [];
  const outgoing = friendLists?.outgoing || [];

  const isFriend = !!targetId && friends.some((x) => String(x.user?.id) === targetId);
  const isIncoming = !!targetId && incoming.some((x) => String(x.requester?.id) === targetId);
  const isOutgoing = !!targetId && outgoing.some((x) => String(x.addressee?.id) === targetId);

  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = React.useState(false);

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body || {}),
    });
    const data = await readJsonSafe(res);
    return { res, data };
  }

  const removeFriend = async () => {
    if (!targetId || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const { res, data } = await postJson('/api/friends/remove', { friendId: targetId });
      if (!res.ok || !data.ok) throw new Error(data.error || 'Could not remove friend');
      if (onReloadFriendLists) await onReloadFriendLists();
      setShowRemoveConfirm(false);
    } catch (e) {
      setErr((e && e.message) || 'Could not update relationship');
    } finally {
      setBusy(false);
    }
  };

  const sendRequest = async () => {
    if (!targetId || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const { res, data } = await postJson('/api/friends/request', { userId: targetId });
      if (!res.ok || !data.ok) throw new Error(data.error || 'Request failed');
      if (onReloadFriendLists) await onReloadFriendLists();
    } catch (e) {
      setErr((e && e.message) || 'Could not update relationship');
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    if (!targetId || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const { res, data } = await postJson('/api/friends/accept', { requesterId: targetId });
      if (!res.ok || !data.ok) throw new Error(data.error || 'Accept failed');
      if (onReloadFriendLists) await onReloadFriendLists();
    } catch (e) {
      setErr((e && e.message) || 'Could not update relationship');
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    if (!targetId || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const { res, data } = await postJson('/api/friends/decline', { requesterId: targetId });
      if (!res.ok || !data.ok) throw new Error(data.error || 'Decline failed');
      if (onReloadFriendLists) await onReloadFriendLists();
    } catch (e) {
      setErr((e && e.message) || 'Could not update relationship');
    } finally {
      setBusy(false);
    }
  };

  if (!viewerId || !targetId || String(viewerId) === targetId) return null;

  return (
    <section className="flique-profile-relationship" aria-label="Relationship">
      {err ? <div className="small text-danger mb-2 text-center">{err}</div> : null}
      <div className="flique-profile-relationship-actions">
        {isFriend ? (
          <button
            type="button"
            className="btn btn-sm btn-flique"
            disabled={busy}
            onClick={() => setShowRemoveConfirm(true)}
          >
            Friends
          </button>
        ) : isIncoming ? (
          <>
            <button type="button" className="btn btn-sm btn-flique" disabled={busy} onClick={accept}>
              Accept
            </button>
            <button type="button" className="btn btn-sm btn-flique-secondary" disabled={busy} onClick={decline}>
              Decline
            </button>
          </>
        ) : isOutgoing ? (
          <span className="small text-muted">Request sent</span>
        ) : (
          <button type="button" className="btn btn-sm btn-flique" disabled={busy} onClick={sendRequest}>
            Add friend
          </button>
        )}
      </div>
      {showRemoveConfirm ? (
        <div className="flique-modal-backdrop">
          <div className="flique-modal card flique-card flique-friends-modal flique-remove-friend-modal">
            <div className="card-body text-center">
              <h2 className="h5 mb-2">Remove friend?</h2>
              <p className="small text-muted mb-3">
                Do you want to remove @{targetUsername} as a friend?
              </p>
              <div className="d-flex gap-2 justify-content-center flique-remove-friend-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light flique-remove-friend-action-btn"
                  onClick={() => setShowRemoveConfirm(false)}
                  disabled={busy}
                >
                  No
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-flique flique-remove-friend-action-btn"
                  onClick={removeFriend}
                  disabled={busy}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

